import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { Order, OrderDetailsVerify } from '~lmo/models/order.model';
import { LmoOrdersService } from '~lmo/services';
import { trackById } from '~utilities/trackById';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-orders-verify-load-details-dialog',
  styleUrls: ['./lmo-orders-verify-load-details-dialog.component.scss'],
  templateUrl: './lmo-orders-verify-load-details-dialog.component.html',
})
export class LmoOrdersVerifyLoadDetailsDialogComponent implements OnInit {
  public currentOrder$: Observable<Order>;
  public verifyLoadDetailsForm: FormGroup;
  public trackById = trackById;
  private completing$$ = new BehaviorSubject<boolean>(false);

  constructor(
    private orderService: LmoOrdersService,
    private fb: FormBuilder,
    public snackBar: MatSnackBar,
    private matDialogRef: MatDialogRef<LmoOrdersVerifyLoadDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public order: Order,
  ) {}

  public get boxes(): FormArray {
    return this.verifyLoadDetailsForm.get('boxes') as FormArray;
  }

  public ngOnInit() {
    this.currentOrder$ = this.orderService.currentOrder$;
    this.buildForm();
  }

  public submitVerification(action: string) {
    const orderDetails: OrderDetailsVerify = this.verifyLoadDetailsForm.value;
    orderDetails.action = action;

    this.completing$$.next(true);
    this.orderService
      .verifyOrder(orderDetails)
      .pipe(
        finalize(() => {
          this.completing$$.next(false);
        }),
      )
      .subscribe(
        (response) => {
          if (response) {
            this.snackBar.open(`Order is verified successfully`, null, {
              duration: 2500,
            });
          }
          this.matDialogRef.close();
        },
        () => {
          this.snackBar.open('Something Went Wrong', null, {
            duration: 2500,
            panelClass: ['snackbar-error'],
          });
        },
      );
  }

  public getWeightInTon(pounds: number): any {
    if (!isNaN(pounds)) {
      if (pounds >= 2000) {
        const tons = pounds / 2000;
        const displayTons = tons % 1 === 0 ? tons : tons.toFixed(2);
        const suffix = tons === 1 ? 'Ton' : 'Tons';
        return `${displayTons} ${suffix}`;
      }
    }
    return null;
  }

  private buildForm() {
    this.verifyLoadDetailsForm = this.fb.group({
      bolNumber: [this.order?.bolNumber, [Validators.required]],
      poNumber: [{ value: this.order?.poNumber, disabled: true }],
    });

    const boxes = this.order?.boxes;
    if (boxes && boxes.length > 0) {
      const boxesArray: FormArray = this.fb.array([]);
      for (const box of boxes) {
        const group = this.fb.group({
          finalLoadWeight: [box.actualLoadWeight, [Validators.required, Validators.min(1), Validators.max(200000)]],
          id: box.id,
        });
        boxesArray.push(group);
      }
      this.verifyLoadDetailsForm.addControl('boxes', boxesArray);
    } else {
      this.verifyLoadDetailsForm.addControl(
        `finalLoadWeight`,
        new FormControl(this.order.actualLoadWeight, [Validators.required, Validators.min(1), Validators.max(200000)]),
      );
    }
  }
}
